
import { Link } from 'react-router-dom';
import { Eye, Edit, Archive, Copy, ArchiveRestore } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Assessment } from '@/types/assessment';
import { useToast } from '@/hooks/use-toast';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface AssessmentCardProps {
  assessment: Assessment;
  onArchive: (id: string) => Promise<void>;
  onUnarchive: (id: string) => Promise<void>;
}

export function AssessmentCard({ assessment, onArchive, onUnarchive }: AssessmentCardProps) {
  const { toast } = useToast();
  
  const copyToClipboard = (text: string, linkType: string, e: React.MouseEvent) => {
    e.preventDefault();
    navigator.clipboard.writeText(text);
    toast({
      title: "Link Copied",
      description: `${linkType} assessment link copied to clipboard!`
    });
  };
  
  const handleArchiveClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    await onArchive(assessment.id);
  };

  const handleUnarchiveClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    await onUnarchive(assessment.id);
  };
  
  const getSiteBaseUrl = () => {
    return window.location.origin;
  };

  return (
    <Card className="assessment-card overflow-hidden h-full">
      <div className="p-4 sm:p-5 lg:p-6 flex flex-col h-full">
        <div className="flex flex-col gap-3 mb-4 flex-1">
          <div className="space-y-2">
            <h3 className="text-lg sm:text-xl font-semibold line-clamp-2 leading-tight">{assessment.title}</h3>
            <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2 leading-relaxed">
              {assessment.description}
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5 sm:gap-2">
            {assessment.isMaturityModel && (
              <Badge variant="outline" className="bg-purple-100 text-purple-800 hover:bg-purple-100 border-none rounded-full px-2 sm:px-3 text-xs">
                Maturity Model
              </Badge>
            )}
            <Badge variant="outline" className={`rounded-full px-2 sm:px-3 text-xs border-none ${assessment.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
              {assessment.isActive ? 'Active' : 'Archived'}
            </Badge>
          </div>
        </div>
        
        {assessment.isActive && (
          <div className="mb-4 space-y-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="w-full justify-center h-10 touch-manipulation"
                    onClick={(e) => copyToClipboard(`${getSiteBaseUrl()}/assessment/${assessment.slug}/free`, "Free version", e)}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    <span className="text-xs sm:text-sm">Copy Free Link</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Copy free version link</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="w-full justify-center h-10 touch-manipulation"
                    onClick={(e) => copyToClipboard(`${getSiteBaseUrl()}/assessment/${assessment.slug}/premium`, "Premium version", e)}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    <span className="text-xs sm:text-sm">Copy Premium Link</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Copy premium version link</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}
        
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-4">
          <div>{assessment.categories.length} categories</div>
          <div>Version {assessment.version}</div>
        </div>
        
        <div className="mt-auto space-y-3">
          <div className="text-xs text-muted-foreground">
            Updated: {new Date(assessment.updatedAt).toLocaleDateString()}
          </div>
          
          <div className="flex justify-end gap-1">
            <Button variant="ghost" size="icon" className="h-10 w-10 touch-manipulation" asChild>
              <Link to={`/admin/assessments/${assessment.id}`} onClick={(e) => e.stopPropagation()}>
                <Eye className="h-4 w-4" />
                <span className="sr-only">View</span>
              </Link>
            </Button>
            {assessment.isActive ? (
              <>
                <Button variant="ghost" size="icon" className="h-10 w-10 touch-manipulation" asChild>
                  <Link to={`/admin/assessments/${assessment.id}/edit`} onClick={(e) => e.stopPropagation()}>
                    <Edit className="h-4 w-4" />
                    <span className="sr-only">Edit</span>
                  </Link>
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="h-10 w-10 touch-manipulation"
                  onClick={handleArchiveClick}
                >
                  <Archive className="h-4 w-4" />
                  <span className="sr-only">Archive</span>
                </Button>
              </>
            ) : (
              <Button 
                variant="ghost" 
                size="icon"
                className="h-10 w-10 text-green-600 hover:text-green-700 hover:bg-green-50 touch-manipulation"
                onClick={handleUnarchiveClick}
              >
                <ArchiveRestore className="h-4 w-4" />
                <span className="sr-only">Unarchive</span>
              </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
